import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import {
  BoxSelection,
  BoxSelectionBoxSize,
  BoxSelectionOptionProps,
  BoxSelectionProps,
  BoxSelectionSelectionType,
} from '..';
import { classes } from './BoxSelection.visual.st.css';
import { boxSelectionDriverFactory } from './BoxSelection.driver';
import { VisualAsync } from '../../test/visual/VisualAsync';
import { VisualTestContainer } from '../../test/visual/VisualTestContainer';

const errorMessage = 'This is an error message';
const getElementFromBelow = () => {
  return (
    <div className={classes.elementBelow}>
      An element below to show the spacing
    </div>
  );
};

visualize('BoxSelection', () => {
  story('simple', () => {
    snap('default props', <BoxSelection name="hours" />);
    snap(
      'checked prop',
      <BoxSelection name="hours">
        <BoxSelection.Option checked id="1" />
        <BoxSelection.Option id="2" />
      </BoxSelection>,
    );
    snap(
      'checked prop with asterisk',
      <BoxSelection name="hours" label="Hours" required={true}>
        <BoxSelection.Option checked id="1" />
        <BoxSelection.Option id="2" />
      </BoxSelection>,
    );
    snap(
      'checked prop and unavailable',
      <BoxSelection name="hours">
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
      </BoxSelection>,
    );
    snap(
      'checked, unavailable and disabled props',
      <BoxSelection name="hours">
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
      </BoxSelection>,
    );
    snap(
      'checked, unavailable and disabled props, and one box not selected',
      <BoxSelection name="hours">
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'checked, unavailable and disabled props, and one box not selected with override styles',
      <BoxSelection name="hours" className={classes.root} label="Pick one">
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'small',
      <BoxSelection name="hours" size={BoxSelectionBoxSize.small}>
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'xsmall',
      <BoxSelection name="hours" size={BoxSelectionBoxSize.xSmall}>
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'inline',
      <BoxSelection name="hours" inline>
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'vertical',
      <BoxSelection name="hours" vertical>
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'error',
      <>
        <BoxSelection name="hours" error errorMessage={errorMessage}>
          <BoxSelection.Option unavailable id="1" />
          <BoxSelection.Option checked id="2" />
          <BoxSelection.Option disabled id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
        {getElementFromBelow()}
      </>,
    );
    snap('focused error', (done) => (
      <VisualAsync
        driverFactory={boxSelectionDriverFactory}
        onDone={done}
        action={async (driver) =>
          (await driver.option.getOptionById('1'))
            .getNative()
            .then((el) => el.focus())
        }
      >
        <BoxSelection name="hours" error={true} errorMessage={errorMessage}>
          <BoxSelection.Option id="1" />
          <BoxSelection.Option checked id="2" />
        </BoxSelection>
      </VisualAsync>
    ));
    snap('focused with override styles', (done) => (
      <VisualAsync
        driverFactory={boxSelectionDriverFactory}
        onDone={done}
        action={async (driver) =>
          (await driver.option.getOptionById('1'))
            .getNative()
            .then((el) => el.focus())
        }
      >
        <BoxSelection name="hours" className={classes.root}>
          <BoxSelection.Option id="1" />
          <BoxSelection.Option checked id="2" />
        </BoxSelection>
      </VisualAsync>
    ));
    snap(
      'error with override styles',
      <>
        <BoxSelection
          name="hours"
          error
          errorMessage={errorMessage}
          className={classes.root}
        >
          <BoxSelection.Option unavailable id="1" />
          <BoxSelection.Option checked id="2" />
          <BoxSelection.Option disabled id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
        {getElementFromBelow()}
      </>,
    );
    snap(
      'vertical error',
      <>
        <BoxSelection name="hours" vertical error errorMessage={errorMessage}>
          <BoxSelection.Option unavailable id="1" />
          <BoxSelection.Option checked id="2" />
          <BoxSelection.Option disabled id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
        {getElementFromBelow()}
      </>,
    );
    snap(
      'inline error',
      <>
        <BoxSelection name="hours" inline error errorMessage={errorMessage}>
          <BoxSelection.Option unavailable id="1" />
          <BoxSelection.Option checked id="2" />
          <BoxSelection.Option disabled id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
        {getElementFromBelow()}
      </>,
    );
    snap(
      'small error',
      <>
        <BoxSelection
          name="hours"
          size={BoxSelectionBoxSize.small}
          error
          errorMessage={errorMessage}
        >
          <BoxSelection.Option unavailable id="1" />
          <BoxSelection.Option checked id="2" />
          <BoxSelection.Option disabled id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
        {getElementFromBelow()}
      </>,
    );
    snap(
      'label + error',
      <BoxSelection
        name="hours"
        label="Pick an hour"
        error
        errorMessage={errorMessage}
      >
        <BoxSelection.Option unavailable id="1" />
        <BoxSelection.Option checked id="2" />
        <BoxSelection.Option disabled id="3" />
        <BoxSelection.Option id="4" />
      </BoxSelection>,
    );
    snap(
      'suffix - horizontal',
      <VisualTestContainer>
        <BoxSelection name="hours" suffix="+3">
          <BoxSelection.Option id="1" />
          <BoxSelection.Option id="2" />
          <BoxSelection.Option id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
      </VisualTestContainer>,
    );
    snap(
      'suffix - vertical',
      <VisualTestContainer>
        <BoxSelection name="hours" vertical suffix="+3">
          <BoxSelection.Option id="1" />
          <BoxSelection.Option id="2" />
          <BoxSelection.Option id="3" />
          <BoxSelection.Option id="4" />
        </BoxSelection>
      </VisualTestContainer>,
    );
  });

  story('simple / focused', () => {
    interface It {
      it: string;
      props: BoxSelectionProps;
      options: BoxSelectionOptionProps[];
    }

    const template: It = {
      it: 'based on checkbox',
      props: {
        name: 'hour-selection',
        label: 'Select time',
      },
      options: [
        { className: 'global-min-width-150px', id: '0', children: '10:00' },
        { className: 'global-min-width-150px', id: '1', children: '10:30' },
        { className: 'global-min-width-150px', id: '2', children: '11:00' },
        { className: 'global-min-width-150px', id: '3', children: '11:30' },
        { className: 'global-min-width-150px', id: '4', children: '12:00' },
        { className: 'global-min-width-150px', id: '5', children: '12:30' },
        { className: 'global-min-width-150px', id: '6', children: '13:00' },
        { className: 'global-min-width-150px', id: '7', children: '13:30' },
      ].map((option) => ({
        ...option,
        type: BoxSelectionSelectionType.CHECKBOX,
      })),
    };

    const suffix: It = {
      it: 'suffix',
      props: {
        name: 'with-suffix',
        label: 'With suffix',
        suffix: <span style={{ alignSelf: 'center' }}>+3</span>,
      },
      options: [
        { className: 'global-min-width-150px', id: '0', children: '10:00' },
        { className: 'global-min-width-150px', id: '1', children: '10:30' },
        { className: 'global-min-width-150px', id: '2', children: '11:00' },
        { className: 'global-min-width-150px', id: '3', children: '11:30' },
        { className: 'global-min-width-150px', id: '4', children: '12:00' },
        { className: 'global-min-width-150px', id: '5', children: '12:30' },
        { className: 'global-min-width-150px', id: '6', children: '13:00' },
        { className: 'global-min-width-150px', id: '7', children: '13:30' },
      ].map((option) => ({
        ...option,
        type: BoxSelectionSelectionType.CHECKBOX,
      })),
    };

    const its: It[] = [
      template,
      {
        ...template,
        it: 'based on radio button',
        options: template.options.map((option) => ({
          ...option,
          type: BoxSelectionSelectionType.RADIO_BUTTON,
        })),
      },
      suffix,
    ];

    its.forEach(({ it, props, options }) =>
      snap(it, (done) => (
        <div style={{ width: '400px' }}>
          <VisualAsync
            driverFactory={boxSelectionDriverFactory}
            onDone={done}
            action={async (driver) =>
              (await driver.option.getOptionById('3'))
                .getNative()
                .then((el) => el.focus())
            }
          >
            <BoxSelection {...props} onChange={() => {}} suffix={props.suffix}>
              {options.map((option) => (
                <BoxSelection.Option
                  key={option.id}
                  {...option}
                  checked={'2' === option.id}
                />
              ))}
            </BoxSelection>
          </VisualAsync>
        </div>
      )),
    );
  });


  story('with wst theme variables', () => {
    snap('default', () => {
      const style = {
        '--wst-primary-background-color-rgb': '0,255,0',
        '--wst-system-disabled-color-rgb': '180,0,230',
        '--wst-paragraph-2-color-rgb': '190,50,130',
      } as React.CSSProperties;

      return (
        <div style={style} >
          <BoxSelection label="Label" name='test' >
            <BoxSelection.Option id="1" />
            <BoxSelection.Option checked id="2" >Option 2</BoxSelection.Option>
            <BoxSelection.Option disabled id="3" >Option 3</BoxSelection.Option>
            <BoxSelection.Option id="4"  >Option 4</BoxSelection.Option>
          </BoxSelection>
        </div>
      );
    });
    snap('error state', () => {
      const style = {
        '--wst-system-error-color-rgb': '0,0,255',
      } as React.CSSProperties;

      return (
        <div style={style} >
          <BoxSelection label="Label" name='test' error errorMessage="Error message" >
            <BoxSelection.Option id="1" />
            <BoxSelection.Option checked id="2" >Option 2</BoxSelection.Option>
            <BoxSelection.Option disabled id="3" >Option 3</BoxSelection.Option>
            <BoxSelection.Option id="4"  >Option 4</BoxSelection.Option>
          </BoxSelection>
        </div>
      );
    });
  });
});
